import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Reveal, SectionHeading } from "./_shared";
import { submitRegistration, logAnalytics } from "../../lib/api";
import { toast } from "sonner";

const interessi = [
    { v: "controllo_scadenze", l: "Controllo delle scadenze" },
    { v: "formazione", l: "Formazione" },
    { v: "sorveglianza_sanitaria", l: "Sorveglianza sanitaria" },
    { v: "sopralluoghi", l: "Sopralluoghi" },
    { v: "documenti_report", l: "Documenti e report" },
    { v: "tutte", l: "Tutte le funzionalità" },
];
const lavoratori = [
    { v: "1-5", l: "1–5" }, { v: "6-15", l: "6–15" }, { v: "16-50", l: "16–50" },
    { v: "51-100", l: "51–100" }, { v: "oltre_100", l: "Oltre 100" },
];

function getUtm() {
    if (typeof window === "undefined") return null;
    const u = new URLSearchParams(window.location.search);
    const keys = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];
    let has = false;
    const out = {};
    keys.forEach((k) => { const v = u.get(k); if (v) { out[k] = v; has = true; } });
    return has ? out : null;
}

export default function RegistrationForm() {
    const nav = useNavigate();
    const [form, setForm] = useState({
        nome: "", cognome: "", azienda: "", ruolo: "", email: "", telefono: "",
        numero_lavoratori: "", interesse: "", note: "",
        consenso_privacy: false, consenso_marketing: false, hp: "",
    });
    const [errors, setErrors] = useState({});
    const [sending, setSending] = useState(false);
    const [started, setStarted] = useState(false);

    const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

    const handleFocus = () => {
        if (!started) {
            setStarted(true);
            logAnalytics("form_start", {});
        }
    };

    const validate = () => {
        const e = {};
        ["nome", "cognome", "azienda", "ruolo"].forEach((f) => { if (!form[f].trim()) e[f] = "Campo obbligatorio"; });
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Email non valida";
        if (!/^[0-9+()\-\s.]{6,25}$/.test(form.telefono)) e.telefono = "Telefono non valido";
        if (!form.consenso_privacy) e.consenso_privacy = "Necessario per iscriverti";
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const submit = async (ev) => {
        ev.preventDefault();
        if (sending) return;
        if (!validate()) return;
        setSending(true);
        try {
            const payload = {
                ...form,
                numero_lavoratori: form.numero_lavoratori || null,
                interesse: form.interesse || null,
                note: form.note || null,
                utm: getUtm(),
            };
            await submitRegistration(payload);
            logAnalytics("form_submit_success", {});
            toast.success("Iscrizione ricevuta! Reindirizzamento…");
            setTimeout(() => nav("/iscrizione-confermata"), 500);
        } catch (err) {
            const msg = err?.response?.data?.detail || "Si è verificato un errore, riprova.";
            logAnalytics("form_submit_error", { message: msg });
            toast.error(msg);
            setSending(false);
        }
    };

    const fieldCls = (name) =>
        `w-full bg-white border rounded-xl px-4 py-3 text-navy-950 placeholder-navy-400 outline-none transition-all focus:border-navy-950 focus:shadow-[0_0_0_4px_rgba(1,38,89,0.08)] ${errors[name] ? "border-red-400" : "border-navy-100"}`;

    return (
        <section id="iscriviti" className="py-24 md:py-32 bg-[linear-gradient(180deg,#FBF3D4_0%,#F4F6FB_60%)]">
            <div className="max-w-[1200px] mx-auto px-5 md:px-8">
                <div className="grid lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] gap-14 items-start">
                    <div>
                        <SectionHeading chapter={9} chapterLabel="Iscrizione" title={<>Iscriviti <span className="text-gold-500">all'evento.</span></>} />
                        <Reveal delay={0.15}>
                            <p className="mt-6 text-navy-700 text-lg leading-relaxed max-w-md">
                                I posti disponibili sono limitati. Compila il modulo per riservare la tua partecipazione alla presentazione ufficiale di SafetyDesk81 del <strong className="text-navy-950">1° ottobre 2026</strong>.
                            </p>
                        </Reveal>
                        <Reveal delay={0.25}>
                            <ul className="mt-8 space-y-3 text-navy-700">
                                {["Conferma via email immediata", "Programma completo della serata", "Posti limitati — prenota per tempo"].map((t, i) => (
                                    <li key={i} className="flex items-start gap-3">
                                        <span className="w-6 h-6 rounded-full bg-navy-950 text-gold-400 flex items-center justify-center flex-shrink-0"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><polyline points="20 6 9 17 4 12" /></svg></span>
                                        <span className="pt-0.5">{t}</span>
                                    </li>
                                ))}
                            </ul>
                            <div className="mt-6 inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest bg-gold-100 text-gold-700 px-3 py-1.5 rounded-full">
                                <span className="w-1.5 h-1.5 rounded-full bg-gold-500" /> Posti limitati
                            </div>
                        </Reveal>
                    </div>

                    <Reveal delay={0.1}>
                        <motion.form
                            onSubmit={submit}
                            onFocus={handleFocus}
                            data-testid="registration-form"
                            className="rounded-3xl bg-white border border-navy-100 shadow-card p-6 md:p-8"
                            noValidate
                        >
                            {/* honeypot */}
                            <input type="text" name="website" value={form.hp} onChange={(e) => set("hp", e.target.value)} tabIndex={-1} autoComplete="off" className="hidden" aria-hidden />

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <Field label="Nome *" name="nome" error={errors.nome}>
                                    <input data-testid="field-nome" className={fieldCls("nome")} value={form.nome} onChange={(e) => set("nome", e.target.value)} placeholder="Il tuo nome" />
                                </Field>
                                <Field label="Cognome *" name="cognome" error={errors.cognome}>
                                    <input data-testid="field-cognome" className={fieldCls("cognome")} value={form.cognome} onChange={(e) => set("cognome", e.target.value)} placeholder="Il tuo cognome" />
                                </Field>
                                <Field label="Azienda *" name="azienda" error={errors.azienda}>
                                    <input data-testid="field-azienda" className={fieldCls("azienda")} value={form.azienda} onChange={(e) => set("azienda", e.target.value)} placeholder="Nome dell'azienda" />
                                </Field>
                                <Field label="Ruolo aziendale *" name="ruolo" error={errors.ruolo}>
                                    <input data-testid="field-ruolo" className={fieldCls("ruolo")} value={form.ruolo} onChange={(e) => set("ruolo", e.target.value)} placeholder="es. RSPP, HR" />
                                </Field>
                                <Field label="Email *" name="email" error={errors.email}>
                                    <input data-testid="field-email" type="email" className={fieldCls("email")} value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="nome@azienda.it" />
                                </Field>
                                <Field label="Telefono *" name="telefono" error={errors.telefono}>
                                    <input data-testid="field-telefono" type="tel" className={fieldCls("telefono")} value={form.telefono} onChange={(e) => set("telefono", e.target.value)} placeholder="+39" />
                                </Field>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                                <Field label="Numero indicativo di lavoratori" name="numero_lavoratori">
                                    <select data-testid="field-lavoratori" className={fieldCls("numero_lavoratori")} value={form.numero_lavoratori} onChange={(e) => set("numero_lavoratori", e.target.value)}>
                                        <option value="">Seleziona…</option>
                                        {lavoratori.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
                                    </select>
                                </Field>
                                <Field label="Quale aspetto ti interessa maggiormente?" name="interesse">
                                    <select data-testid="field-interesse" className={fieldCls("interesse")} value={form.interesse} onChange={(e) => set("interesse", e.target.value)}>
                                        <option value="">Seleziona…</option>
                                        {interessi.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
                                    </select>
                                </Field>
                            </div>

                            <Field label="Domande o richieste particolari" name="note" className="mt-4">
                                <textarea data-testid="field-note" className={`${fieldCls("note")} resize-none`} rows="3" value={form.note} onChange={(e) => set("note", e.target.value)} placeholder="Facoltativo" />
                            </Field>

                            <div className="mt-6 space-y-3">
                                <label className={`flex items-start gap-3 text-sm ${errors.consenso_privacy ? "text-red-600" : "text-navy-700"}`}>
                                    <input data-testid="consent-privacy" type="checkbox" className="mt-0.5 accent-navy-950 w-4 h-4" checked={form.consenso_privacy} onChange={(e) => set("consenso_privacy", e.target.checked)} />
                                    <span>
                                        Dichiaro di aver letto l'<a href="/privacy" target="_blank" rel="noreferrer" className="underline hover:text-navy-950">informativa privacy</a> e acconsento al trattamento dei dati per la gestione dell'iscrizione all'evento. *
                                    </span>
                                </label>
                                <label className="flex items-start gap-3 text-sm text-navy-700">
                                    <input data-testid="consent-marketing" type="checkbox" className="mt-0.5 accent-navy-950 w-4 h-4" checked={form.consenso_marketing} onChange={(e) => set("consenso_marketing", e.target.checked)} />
                                    <span>Desidero ricevere aggiornamenti e comunicazioni relative a SafetyDesk81 e ai servizi di Centro Sicurezza Consulenze.</span>
                                </label>
                            </div>

                            <button
                                data-testid="submit-registration"
                                disabled={sending}
                                type="submit"
                                className="mt-8 w-full bg-navy-950 hover:bg-navy-900 disabled:opacity-70 text-white font-semibold py-4 rounded-full transition-all duration-300 hover:-translate-y-0.5 shadow-card inline-flex items-center justify-center gap-2"
                            >
                                {sending ? (
                                    <><span className="w-4 h-4 rounded-full border-2 border-white/30 border-t-white animate-spin" /> Invio in corso…</>
                                ) : (
                                    <>Prenota il tuo posto
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round"><path d="M5 12h14M13 5l7 7-7 7" /></svg>
                                    </>
                                )}
                            </button>
                            <p className="mt-3 text-center text-sm text-navy-500">Riceverai una conferma all'indirizzo email indicato.</p>
                        </motion.form>
                    </Reveal>
                </div>
            </div>
        </section>
    );
}

function Field({ label, name, error, children, className = "" }) {
    return (
        <label className={`block ${className}`}>
            <span className="block text-xs font-semibold text-navy-700 mb-1.5 uppercase tracking-wider">{label}</span>
            {children}
            {error && <span data-testid={`err-${name}`} className="block text-xs text-red-500 mt-1.5">{error}</span>}
        </label>
    );
}
